import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import {
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import { RecallPermissionKind } from "@/modules/electron/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  restartRequired: boolean;
  onRestart: () => void;
};

export const VoicePermissionsModalContent: FC<Props> = ({
  isOpen,
  onClose,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
  restartRequired,
  onRestart,
}) => {
  const items = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Permission,
        id: "microphone",
        title: "Microphone",
        description: "Mem relies on your microphone to capture your voice",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Microphone,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Microphone,
          }),
      },
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-screen-recording",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "screen-recording",
        title: "System audio recording only",
        description:
          "Mem relies on this permission to capture other people's voices during meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.SystemAudio }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.SystemAudio,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.SystemAudio,
          }),
      },
    ];

    if (restartRequired) {
      itemList.push({
        kind: ConfigurationListItemKind.Divider,
        id: "divider-restart",
      });

      itemList.push({
        kind: ConfigurationListItemKind.Button,
        id: "restart",
        title: "Restart Mem",
        description: "To complete setup, restart Mem.",
        actionLabel: "Restart",
        onClick: onRestart,
      });
    }

    return itemList;
  }, [
    handleEnablePermission,
    handleOpenPermissionPane,
    onRestart,
    resolveConfigurationStatus,
    restartRequired,
  ]);

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <Header slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          Set up Voice Mode
        </MDSText>
      </Header>

      <DialogContent slot={MDSDialogSlotName.Content}>
        <Description variant={MDSTextVariant.ParagraphS}>
          Mem can record and create notes from meeting audio, podcasts, or simply your own voice.
          Grant the required permissions in your system settings.
        </Description>

        <ConfigurationList items={items} />
      </DialogContent>
    </MDSModalDialog>
  );
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: ${({ theme }) => theme.spacing["2"]};
`;

const DialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["6"]};
`;

const Description = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;
